import type { CubeLut } from './compositing';
import { cubeToText } from './compositing';

/**
 * Colour grading in the style of DaVinci Resolve's colour page: primary wheels
 * (lift / gamma / gain / offset), tint, shadows / highlights, colour boost,
 * custom curves and the hue / luma curves. Settings live in a clip's `ext.grade`
 * (absent = neutral).
 *
 * The whole grade is a per-pixel function of the colour, so both renderers
 * apply it as a 3D LUT baked by `bakeGrade` from the same code: the preview
 * uploads it as a 3D texture, the export writes it as a .cube file for
 * `lut3d`. That keeps the two in parity by construction. It runs after the
 * basic adjustments (brightness … temperature and the look preset) and before
 * an uploaded LUT. Contrast pivot is not part of it: it shifts the basic
 * contrast (see resolveLook).
 */

/** A curve point, [x, y] (both 0..1 for custom curves; see each curve for y). */
export type CurvePoint = [number, number];

/** One colour wheel: per-channel amounts plus the master (luminance) slider; all 0 = neutral. */
export interface Wheel {
  r: number;
  g: number;
  b: number;
  y: number;
}

export interface Grade {
  /** Raises or lowers the black point (white stays put). */
  lift: Wheel;
  /** Bends the midtones (exponent 4^-v): positive brightens. */
  gamma: Wheel;
  /** Scales towards white in stops (2^v); black stays put. */
  gain: Wheel;
  /** Shifts everything. */
  offset: Wheel;
  /** Contrast pivot (0..1, default 0.5): the level the basic contrast turns around. */
  pivot: number;
  /** -1 (green) .. 1 (magenta). */
  tint: number;
  /** -1..1: darkens / brightens the shadows (about ±1.5 stops near black). */
  shadows: number;
  /** -1..1: recovers / brightens the highlights. */
  highlights: number;
  /** -1..1: saturation that favours the less saturated colours (vibrance). */
  colorBoost: number;
  /** Custom curves (input → output, 0..1); [] = straight. Master first, then each channel. */
  curves: { master: CurvePoint[]; r: CurvePoint[]; g: CurvePoint[]; b: CurvePoint[] };
  /** Hue vs hue: x = hue (0..1, red at 0), y = shift −1..1 (±180°). Periodic; [] = flat. */
  hueHue: CurvePoint[];
  /** Hue vs saturation: y = −1..1 (saturation × 1 + y). */
  hueSat: CurvePoint[];
  /** Hue vs luminance: y = −1..1 (brightness of that hue, scaled by its saturation). */
  hueLum: CurvePoint[];
  /** Luminance vs saturation: x = luma, y = −1..1 (saturation × 1 + y). */
  lumSat: CurvePoint[];
  /**
   * Log wheels (Resolve style): offsets limited to the shadows, midtones and
   * highlights. The split between them is set by `logLow` / `logHigh` (luma
   * levels; each boundary blends over ±LOG_SOFT).
   */
  logShadow: Wheel;
  logMid: Wheel;
  logHighlight: Wheel;
  /** Shadows end / midtones start (0..1, default 0.333). */
  logLow: number;
  /** Midtones end / highlights start (0..1, default 0.55). */
  logHigh: number;
  /** RGB mixer: 3×3, row-major (output red from input r, g, b; then green; then blue). Identity = neutral. */
  mixer: number[];
  /** Keep each pixel's luma while mixing (monochrome and channel swaps without a brightness change). */
  mixerPreserve: boolean;
}

export type PrimaryWheelId = 'lift' | 'gamma' | 'gain' | 'offset';
export type LogWheelId = 'logShadow' | 'logMid' | 'logHighlight';
export type WheelId = PrimaryWheelId | LogWheelId;
export const WHEEL_IDS: readonly PrimaryWheelId[] = ['lift', 'gamma', 'gain', 'offset'];
export const LOG_WHEEL_IDS: readonly LogWheelId[] = ['logShadow', 'logMid', 'logHighlight'];
const ALL_WHEEL_IDS: readonly WheelId[] = [...WHEEL_IDS, ...LOG_WHEEL_IDS];
export type CurveChannel = 'master' | 'r' | 'g' | 'b';
export const CURVE_CHANNELS: readonly CurveChannel[] = ['master', 'r', 'g', 'b'];
export type HueCurveId = 'hueHue' | 'hueSat' | 'hueLum' | 'lumSat';
export const HUE_CURVE_IDS: readonly HueCurveId[] = ['hueHue', 'hueSat', 'hueLum', 'lumSat'];

/**
 * Per-wheel ranges: `master` for the luminance slider, `color` for each
 * channel. The puck at the rim of the wheel gives a chroma of `color / 2`.
 */
export const WHEEL_RANGES: Record<WheelId, { master: number; color: number; label: string }> = {
  lift: { master: 1, color: 0.25, label: 'Lift' },
  gamma: { master: 1, color: 0.25, label: 'Gamma' },
  gain: { master: 2, color: 0.5, label: 'Gain' },
  offset: { master: 1, color: 0.25, label: 'Offset' },
  logShadow: { master: 0.5, color: 0.2, label: 'Shadow' },
  logMid: { master: 0.5, color: 0.2, label: 'Midtone' },
  logHighlight: { master: 0.5, color: 0.2, label: 'Highlight' },
};

/** Default log wheel ranges and the half-width of the blend at each boundary. */
export const LOG_LOW = 0.333;
export const LOG_HIGH = 0.55;
export const LOG_SOFT = 0.1;
export const IDENTITY_MIXER: readonly number[] = [1, 0, 0, 0, 1, 0, 0, 0, 1];
export const mixerIsIdentity = (m: readonly number[]) => m.every((v, i) => Math.abs(v - IDENTITY_MIXER[i]) < EPS);

const ZERO_WHEEL: Wheel = { r: 0, g: 0, b: 0, y: 0 };

export const NEUTRAL_GRADE: Grade = {
  lift: ZERO_WHEEL,
  gamma: ZERO_WHEEL,
  gain: ZERO_WHEEL,
  offset: ZERO_WHEEL,
  pivot: 0.5,
  tint: 0,
  shadows: 0,
  highlights: 0,
  colorBoost: 0,
  curves: { master: [], r: [], g: [], b: [] },
  hueHue: [],
  hueSat: [],
  hueLum: [],
  lumSat: [],
  logShadow: ZERO_WHEEL,
  logMid: ZERO_WHEEL,
  logHighlight: ZERO_WHEEL,
  logLow: LOG_LOW,
  logHigh: LOG_HIGH,
  mixer: [...IDENTITY_MIXER],
  mixerPreserve: false,
};

/** What `ext.grade` stores: only the parts that differ from neutral. */
export type GradeData = Partial<Omit<Grade, 'curves'>> & { curves?: Partial<Grade['curves']> };

const MAX_POINTS = 16;
const EPS = 1e-6;

const num = (v: unknown, lo: number, hi: number): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : undefined;

function sanitizeWheel(raw: unknown, id: WheelId): Wheel | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const { master, color } = WHEEL_RANGES[id];
  const w = { r: num(o.r, -color, color) ?? 0, g: num(o.g, -color, color) ?? 0, b: num(o.b, -color, color) ?? 0, y: num(o.y, -master, master) ?? 0 };
  return wheelIsNeutral(w) ? undefined : w;
}

/** Sorted points inside the curve's box, at most MAX_POINTS, no duplicate x. */
function sanitizePoints(raw: unknown, yLo: number, yHi: number): CurvePoint[] {
  if (!Array.isArray(raw)) return [];
  const pts: CurvePoint[] = [];
  for (const p of raw.slice(0, MAX_POINTS)) {
    if (!Array.isArray(p)) continue;
    const x = num(p[0], 0, 1);
    const y = num(p[1], yLo, yHi);
    if (x === undefined || y === undefined) continue;
    pts.push([Math.round(x * 1e4) / 1e4, Math.round(y * 1e4) / 1e4]);
  }
  pts.sort((a, b) => a[0] - b[0]);
  return pts.filter((p, i) => i === 0 || p[0] - pts[i - 1][0] > 1e-4);
}

export const wheelIsNeutral = (w: Wheel) => Math.abs(w.r) < EPS && Math.abs(w.g) < EPS && Math.abs(w.b) < EPS && Math.abs(w.y) < EPS;

/** A custom curve that leaves values unchanged (none, or only the two corners). */
export function curveIsIdentity(pts: readonly CurvePoint[]): boolean {
  if (!pts.length) return true;
  return pts.length >= 2 && pts[0][0] < 1e-4 && pts[pts.length - 1][0] > 1 - 1e-4 && pts.every((p) => Math.abs(p[0] - p[1]) < 1e-4);
}
export const flatCurve = (pts: readonly CurvePoint[]) => pts.every((p) => Math.abs(p[1]) < 1e-4);

/** Keeps known fields with valid values; neutral parts are dropped (undefined = all neutral). */
export function sanitizeGrade(raw: unknown): GradeData | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const out: GradeData = {};
  for (const id of ALL_WHEEL_IDS) {
    const w = sanitizeWheel(r[id], id);
    if (w) out[id] = w;
  }
  const lo = num(r.logLow, 0.01, 0.98) ?? LOG_LOW;
  const hi = Math.max(lo + 0.01, num(r.logHigh, 0.02, 0.99) ?? LOG_HIGH);
  if (Math.abs(lo - LOG_LOW) > EPS) out.logLow = lo;
  if (Math.abs(hi - LOG_HIGH) > EPS) out.logHigh = hi;
  if (Array.isArray(r.mixer) && r.mixer.length === 9) {
    const m = r.mixer.map((v, i) => Math.round((num(v, -2, 2) ?? IDENTITY_MIXER[i]) * 1e4) / 1e4);
    if (!mixerIsIdentity(m)) {
      out.mixer = m;
      if (r.mixerPreserve === true) out.mixerPreserve = true;
    }
  }
  const pivot = num(r.pivot, 0, 1);
  if (pivot !== undefined && Math.abs(pivot - 0.5) > EPS) out.pivot = pivot;
  for (const k of ['tint', 'shadows', 'highlights', 'colorBoost'] as const) {
    const v = num(r[k], -1, 1);
    if (v) out[k] = v;
  }
  const curves = r.curves && typeof r.curves === 'object' ? (r.curves as Record<string, unknown>) : {};
  for (const ch of CURVE_CHANNELS) {
    const pts = sanitizePoints(curves[ch], 0, 1);
    if (!curveIsIdentity(pts)) (out.curves ??= {})[ch] = pts;
  }
  for (const id of HUE_CURVE_IDS) {
    const pts = sanitizePoints(r[id], -1, 1);
    if (!flatCurve(pts)) out[id] = pts;
  }
  return Object.keys(out).length ? out : undefined;
}

/** The full grade (neutral defaults filled in) from stored data. */
export function fullGrade(d: GradeData | undefined | null): Grade {
  if (!d) return NEUTRAL_GRADE;
  return {
    ...NEUTRAL_GRADE,
    ...d,
    curves: { ...NEUTRAL_GRADE.curves, ...d.curves },
  } as Grade;
}

/** Whether the per-pixel grade (everything but the pivot) changes nothing. */
export function gradeIsNeutral(g: Grade): boolean {
  return (
    ALL_WHEEL_IDS.every((id) => wheelIsNeutral(g[id])) &&
    mixerIsIdentity(g.mixer) &&
    Math.abs(g.tint) < EPS &&
    Math.abs(g.shadows) < EPS &&
    Math.abs(g.highlights) < EPS &&
    Math.abs(g.colorBoost) < EPS &&
    CURVE_CHANNELS.every((ch) => curveIsIdentity(g.curves[ch])) &&
    HUE_CURVE_IDS.every((id) => flatCurve(g[id]))
  );
}

// ---------------------------------------------------------------------------
// Curves: monotone cubic (Fritsch–Carlson), so curves never overshoot their points
// ---------------------------------------------------------------------------

export type CurveFn = (x: number) => number;

function monotone(xs: number[], ys: number[]): CurveFn {
  const n = xs.length;
  if (n === 0) return () => 0;
  if (n === 1) return () => ys[0];
  const d: number[] = [];
  for (let i = 0; i < n - 1; i++) d.push((ys[i + 1] - ys[i]) / Math.max(EPS, xs[i + 1] - xs[i]));
  const m: number[] = new Array(n);
  m[0] = d[0];
  m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) m[i] = d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2;
  for (let i = 0; i < n - 1; i++) {
    if (Math.abs(d[i]) < EPS) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = m[i] / d[i];
    const b = m[i + 1] / d[i];
    const s = a * a + b * b;
    if (s > 9) {
      const t = 3 / Math.sqrt(s);
      m[i] = t * a * d[i];
      m[i + 1] = t * b * d[i];
    }
  }
  return (x) => {
    if (x <= xs[0]) return ys[0];
    if (x >= xs[n - 1]) return ys[n - 1];
    let i = 0;
    while (i < n - 2 && x > xs[i + 1]) i++;
    const h = xs[i + 1] - xs[i];
    const t = (x - xs[i]) / h;
    const t2 = t * t;
    const t3 = t2 * t;
    return (2 * t3 - 3 * t2 + 1) * ys[i] + (t3 - 2 * t2 + t) * h * m[i] + (-2 * t3 + 3 * t2) * ys[i + 1] + (t3 - t2) * h * m[i + 1];
  };
}

/** A custom curve (input → output); points outside 0..1 hold the end values. */
export function customCurve(pts: readonly CurvePoint[]): CurveFn {
  if (curveIsIdentity(pts)) return (x) => x;
  const f = monotone(
    pts.map((p) => p[0]),
    pts.map((p) => p[1]),
  );
  return (x) => Math.min(1, Math.max(0, f(x)));
}

/** A hue curve, periodic over 0..1 (the points wrap around red). */
export function hueCurve(pts: readonly CurvePoint[]): CurveFn {
  if (flatCurve(pts)) return () => 0;
  if (pts.length === 1) return () => pts[0][1];
  const ext = [[pts[pts.length - 2][0] - 1, pts[pts.length - 2][1]], [pts[pts.length - 1][0] - 1, pts[pts.length - 1][1]], ...pts, [pts[0][0] + 1, pts[0][1]], [pts[1][0] + 1, pts[1][1]]];
  const f = monotone(
    ext.map((p) => p[0]),
    ext.map((p) => p[1]),
  );
  return (x) => Math.min(1, Math.max(-1, f(x - Math.floor(x))));
}

/** The luma vs saturation curve (flat beyond its first and last points). */
export function lumaCurve(pts: readonly CurvePoint[]): CurveFn {
  if (flatCurve(pts)) return () => 0;
  const f = monotone(
    pts.map((p) => p[0]),
    pts.map((p) => p[1]),
  );
  return (x) => Math.min(1, Math.max(-1, f(x)));
}

// ---------------------------------------------------------------------------
// The per-pixel grade
// ---------------------------------------------------------------------------

/** BT.601 luma weights (as the preview's YUV helpers and ffmpeg eq use). */
export const LUMA_WEIGHTS: [number, number, number] = [0.299, 0.587, 0.114];
const luma = (r: number, g: number, b: number) => 0.299 * r + 0.587 * g + 0.114 * b;

/** GLSL smoothstep (0 below a, 1 above b). */
export function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

/** HSV hue (0..1, red at 0) of a colour. */
export function hueOf(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const c = max - min;
  if (c < 1e-9) return 0;
  let h: number;
  if (max === r) h = ((g - b) / c) % 6;
  else if (max === g) h = (b - r) / c + 2;
  else h = (r - g) / c + 4;
  h /= 6;
  return h < 0 ? h + 1 : h;
}

export type PixelFn = (r: number, g: number, b: number) => [number, number, number];
export type PixelInto = (r: number, g: number, b: number, out: Float32Array | number[], o: number) => void;

/** Compiles a grade into a pixel function (0..1 RGB in and out, clamped). */
export function compileGrade(g: Grade): PixelFn {
  const f = compileGradeInto(g);
  return (r, gg, b) => {
    const out = [0, 0, 0];
    f(r, gg, b, out, 0);
    return out as [number, number, number];
  };
}

/** The pixel function writing into `out` at `o` (no allocation per pixel, for baking). */
export function compileGradeInto(g: Grade): PixelInto {
  const ch = (w: Wheel) => [w.y + w.r, w.y + w.g, w.y + w.b];
  const off = ch(g.offset);
  const lift = ch(g.lift);
  const gain = ch(g.gain).map((v) => Math.pow(2, v));
  const gam = ch(g.gamma).map((v) => Math.pow(4, -v));
  // Tint: green ↔ magenta, normalised so white keeps its brightness.
  const tf = [1 + 0.1 * g.tint, 1 - 0.2 * g.tint, 1 + 0.1 * g.tint];
  const tl = luma(tf[0], tf[1], tf[2]);
  const tint = tf.map((v) => v / tl);
  const hasTint = Math.abs(g.tint) > EPS;
  const sh = g.shadows * 1.5;
  const hi = g.highlights * 1.5;
  const master = customCurve(g.curves.master);
  const rgbCurves = [customCurve(g.curves.r), customCurve(g.curves.g), customCurve(g.curves.b)];
  const hasCurves = CURVE_CHANNELS.some((c) => !curveIsIdentity(g.curves[c]));
  const hh = hueCurve(g.hueHue);
  const hs = hueCurve(g.hueSat);
  const hl = hueCurve(g.hueLum);
  const ls = lumaCurve(g.lumSat);
  const hasHue = HUE_CURVE_IDS.some((id) => !flatCurve(g[id]));
  const boost = g.colorBoost;
  const logS = ch(g.logShadow);
  const logM = ch(g.logMid);
  const logH = ch(g.logHighlight);
  const hasLog = LOG_WHEEL_IDS.some((id) => !wheelIsNeutral(g[id]));
  const [logLo, logHi] = [g.logLow, g.logHigh];
  const mx = g.mixer;
  const hasMixer = !mixerIsIdentity(mx);
  const c = new Float64Array(3);
  return (r0, g0, b0, out, o) => {
    c[0] = r0;
    c[1] = g0;
    c[2] = b0;
    for (let i = 0; i < 3; i++) {
      let x = c[i] + off[i];
      x = lift[i] + x * (gain[i] - lift[i]);
      if (x > 0) x = Math.pow(x, gam[i]);
      c[i] = x;
    }
    if (hasLog) {
      const y = luma(c[0], c[1], c[2]);
      const ws = 1 - smoothstep(logLo - LOG_SOFT, logLo + LOG_SOFT, y);
      const wh = smoothstep(logHi - LOG_SOFT, logHi + LOG_SOFT, y);
      const wm = Math.max(0, 1 - ws - wh);
      for (let i = 0; i < 3; i++) c[i] += ws * logS[i] + wm * logM[i] + wh * logH[i];
    }
    if (hasMixer) {
      const [r, gg, b] = [c[0], c[1], c[2]];
      c[0] = mx[0] * r + mx[1] * gg + mx[2] * b;
      c[1] = mx[3] * r + mx[4] * gg + mx[5] * b;
      c[2] = mx[6] * r + mx[7] * gg + mx[8] * b;
      if (g.mixerPreserve) {
        const d = luma(r, gg, b) - luma(c[0], c[1], c[2]);
        c[0] += d;
        c[1] += d;
        c[2] += d;
      }
    }
    if (hasTint) for (let i = 0; i < 3; i++) c[i] *= tint[i];
    if (sh || hi) {
      const y = luma(c[0], c[1], c[2]);
      let y1 = y;
      if (sh && y > 0 && y < 1) y1 = y * Math.pow(2, sh * (1 - y) * (1 - y));
      let y2 = y1;
      if (hi && y1 > 0 && y1 < 1) y2 = 1 - (1 - y1) * Math.pow(2, -hi * y1 * y1);
      const d = y2 - y;
      c[0] += d;
      c[1] += d;
      c[2] += d;
    }
    if (hasCurves) for (let i = 0; i < 3; i++) c[i] = rgbCurves[i](master(Math.min(1, Math.max(0, c[i]))));
    if (hasHue) {
      for (let i = 0; i < 3; i++) c[i] = Math.min(1, Math.max(0, c[i]));
      const h = hueOf(c[0], c[1], c[2]);
      const chroma = Math.max(c[0], c[1], c[2]) - Math.min(c[0], c[1], c[2]);
      let y = luma(c[0], c[1], c[2]);
      // Hue shift: rotate the chroma in the YCbCr plane (keeps luma; same as the hue control).
      const shift = hh(h) * Math.PI;
      if (shift) {
        const u = (c[2] - y) * 0.564;
        const v = (c[0] - y) * 0.713;
        const cs = Math.cos(shift);
        const sn = Math.sin(shift);
        c[0] = y + 1.403 * (u * sn + v * cs);
        c[2] = y + 1.773 * (u * cs - v * sn);
        c[1] = (y - 0.299 * c[0] - 0.114 * c[2]) / 0.587;
      }
      const dl = hl(h) * 0.5 * chroma;
      if (dl) {
        c[0] += dl;
        c[1] += dl;
        c[2] += dl;
        y += dl;
      }
      const m = Math.max(0, (1 + hs(h)) * (1 + ls(Math.min(1, Math.max(0, y)))));
      if (Math.abs(m - 1) > EPS) for (let i = 0; i < 3; i++) c[i] = y + (c[i] - y) * m;
    }
    if (boost) {
      const y = luma(c[0], c[1], c[2]);
      const chroma = Math.min(1, Math.max(c[0], c[1], c[2]) - Math.min(c[0], c[1], c[2]));
      const m = Math.max(0, 1 + boost * (1 - chroma));
      for (let i = 0; i < 3; i++) c[i] = y + (c[i] - y) * m;
    }
    out[o] = Math.min(1, Math.max(0, c[0]));
    out[o + 1] = Math.min(1, Math.max(0, c[1]));
    out[o + 2] = Math.min(1, Math.max(0, c[2]));
  };
}

/** One pixel through a grade (tests and tools; renderers bake a LUT). */
export function gradePixel(g: Grade, r: number, gr: number, b: number): [number, number, number] {
  return compileGrade(g)(r, gr, b);
}

/** LUT sizes: still grades get a fine LUT; animated ones change per frame, so a lighter one. */
export const GRADE_LUT_SIZE = 33;
export const GRADE_LUT_SIZE_ANIMATED = 17;

/** The grade as a 3D LUT (red fastest, then green, then blue — the .cube order). */
export function bakeGrade(g: Grade, size = GRADE_LUT_SIZE): CubeLut {
  const f = compileGradeInto(g);
  const data = new Float32Array(size * size * size * 3);
  let i = 0;
  for (let b = 0; b < size; b++) {
    for (let gg = 0; gg < size; gg++) {
      for (let r = 0; r < size; r++, i += 3) f(r / (size - 1), gg / (size - 1), b / (size - 1), data, i);
    }
  }
  return { size, data, domainMin: [0, 0, 0], domainMax: [1, 1, 1] };
}

/**
 * Trilinear lookup in a 0..1-domain LUT: what the preview's 3D texture (linear
 * filtering) and ffmpeg `lut3d=interp=trilinear` both compute.
 */
export function sampleCube(lut: CubeLut, r: number, g: number, b: number): [number, number, number] {
  const n = lut.size - 1;
  const p = [r, g, b].map((v) => Math.min(1, Math.max(0, v)) * n);
  const i0 = p.map((v) => Math.min(n - 1, Math.floor(v)));
  const f = p.map((v, k) => v - i0[k]);
  const out: [number, number, number] = [0, 0, 0];
  for (let corner = 0; corner < 8; corner++) {
    const dr = corner & 1;
    const dg = (corner >> 1) & 1;
    const db = (corner >> 2) & 1;
    const w = (dr ? f[0] : 1 - f[0]) * (dg ? f[1] : 1 - f[1]) * (db ? f[2] : 1 - f[2]);
    if (!w) continue;
    const idx = ((i0[2] + db) * lut.size * lut.size + (i0[1] + dg) * lut.size + (i0[0] + dr)) * 3;
    for (let c = 0; c < 3; c++) out[c] += w * lut.data[idx + c];
  }
  return out;
}

export function gradeCube(g: Grade, size = GRADE_LUT_SIZE): string {
  return cubeToText(bakeGrade(g, size));
}

/** A stable key of a grade's per-pixel part (for caching baked LUTs). */
export function gradeKey(g: Grade): string {
  const { pivot: _pivot, ...rest } = g;
  return JSON.stringify(rest);
}

// ---------------------------------------------------------------------------
// Wheels ↔ puck position (vectorscope orientation: Cb right, Cr up)
// ---------------------------------------------------------------------------

/** Puck position (−1..1 each, inside the unit circle) of a wheel's colour. */
export function wheelPuck(w: Wheel, id: WheelId): { x: number; y: number } {
  const rim = WHEEL_RANGES[id].color / 2;
  const y = luma(w.r, w.g, w.b);
  return { x: ((w.b - y) * 0.564) / rim, y: ((w.r - y) * 0.713) / rim };
}

/** The wheel colour for a puck position (luma-neutral; keeps the master). */
export function wheelFromPuck(x: number, y: number, id: WheelId, master: number): Wheel {
  const d = Math.hypot(x, y);
  if (d > 1) {
    x /= d;
    y /= d;
  }
  const { color } = WHEEL_RANGES[id];
  const rim = color / 2;
  const cb = x * rim;
  const cr = y * rim;
  const r = 1.403 * cr;
  const b = 1.773 * cb;
  const g = (-0.299 * r - 0.114 * b) / 0.587;
  const clamp = (v: number) => Math.round(Math.min(color, Math.max(-color, v)) * 1e4) / 1e4;
  return { r: clamp(r), g: clamp(g), b: clamp(b), y: master };
}

// ---------------------------------------------------------------------------
// Effects (ext.fx): film grain, glow, chromatic aberration, lens distortion
// ---------------------------------------------------------------------------

export interface GlowFx {
  /** 0..1 how much of the glow is screened over the picture. */
  amount: number;
  /** 0..1 level above which pixels glow. */
  threshold: number;
  /** Blur radius (gaussian sigma) in project pixels. */
  radius: number;
}

export interface VideoEffects {
  /** −1..1 midtone detail: local contrast in the midtones (negative softens). Not keyframable. */
  detail?: number;
  /** 0..1 film grain (monochrome, changes every frame). */
  grain?: number;
  /** Grain cell size in project pixels, 1..8 (absent = 1: one value per pixel). */
  grainSize?: number;
  /** 0..1 grain softness: a gaussian of 2 × softness × size project pixels over the grain (absent = sharp). */
  grainSoftness?: number;
  /** Grain strength in the shadows, midtones and highlights, 0..2 each (absent = 1, 1, 1). */
  grainTones?: [number, number, number];
  /** How the grain combines with the picture (absent = added evenly). */
  grainBlend?: GrainBlend;
  glow?: GlowFx;
  /** 0..1 chromatic aberration: red and blue split sideways by up to 1% of the width each. */
  aberration?: number;
  /** Radial: the channels scale about the centre, the split growing towards the edges (absent = sideways). */
  aberrationMode?: 'radial';
  /** How far each of R, G, B moves, −2..2 (absent = 1, 0, −1: red out / right, blue in / left). */
  aberrationRgb?: [number, number, number];
  /** −1..1 lens distortion: positive bulges (barrel), negative pinches (pincushion). */
  lens?: number;
}

export const DEFAULT_GLOW: GlowFx = { amount: 0.5, threshold: 0.6, radius: 20 };

/** Grain blend modes: film embeds the grain (stronger in darker tones), overlay keeps it to the midtones. */
export const GRAIN_BLENDS = ['film', 'overlay'] as const;
export type GrainBlend = (typeof GRAIN_BLENDS)[number];
export const DEFAULT_ABERRATION_RGB: [number, number, number] = [1, 0, -1];

export function sanitizeEffects(raw: unknown): VideoEffects | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const out: VideoEffects = {};
  const detail = num(r.detail, -1, 1);
  if (detail) out.detail = detail;
  const grain = num(r.grain, 0, 1);
  if (grain) out.grain = grain;
  const gs = num(r.grainSize, 1, 8);
  if (gs !== undefined && gs > 1) out.grainSize = Math.round(gs * 100) / 100;
  const soft = num(r.grainSoftness, 0, 1);
  if (soft) out.grainSoftness = soft;
  const triple = (v: unknown, lo: number, hi: number): [number, number, number] | undefined =>
    Array.isArray(v) && v.length === 3 && v.every((x) => typeof x === 'number' && Number.isFinite(x))
      ? (v.map((x) => Math.min(hi, Math.max(lo, x as number))) as [number, number, number])
      : undefined;
  const tones = triple(r.grainTones, 0, 2);
  if (tones && tones.some((t) => t !== 1)) out.grainTones = tones;
  if ((GRAIN_BLENDS as readonly unknown[]).includes(r.grainBlend)) out.grainBlend = r.grainBlend as GrainBlend;
  const g = r.glow && typeof r.glow === 'object' ? (r.glow as Record<string, unknown>) : null;
  if (g) {
    out.glow = { amount: num(g.amount, 0, 1) ?? DEFAULT_GLOW.amount, threshold: num(g.threshold, 0, 0.99) ?? DEFAULT_GLOW.threshold, radius: num(g.radius, 0.5, 200) ?? DEFAULT_GLOW.radius };
  }
  const ab = num(r.aberration, 0, 1);
  if (ab) out.aberration = ab;
  if (r.aberrationMode === 'radial') out.aberrationMode = 'radial';
  const rgb = triple(r.aberrationRgb, -2, 2);
  if (rgb && rgb.some((v, i) => v !== DEFAULT_ABERRATION_RGB[i])) out.aberrationRgb = rgb;
  const lens = num(r.lens, -1, 1);
  if (lens) out.lens = lens;
  return Object.keys(out).length ? out : undefined;
}

/** Blur radius (gaussian sigma, project pixels) that midtone detail compares against. */
export const DETAIL_RADIUS = 10;

/**
 * Midtone detail per channel (0..1): the difference from the blurred picture,
 * scaled by `amount` and weighted towards the midtones (ffmpeg lut2 runs the
 * same expression in 8-bit levels).
 */
export function detailChannel(c: number, blurred: number, amount: number): number {
  const w = 1 - (2 * c - 1) * (2 * c - 1);
  return Math.min(1, Math.max(0, c + (c - blurred) * amount * 1.5 * w));
}

/** Grain strength in 8-bit levels (ffmpeg `noise` strength: uniform ±strength/2). */
export const grainStrength = (amount: number) => Math.round(Math.min(1, Math.max(0, amount)) * 60);

/** Chromatic aberration shift as a fraction of the clip width. */
export const aberrationShift = (amount: number) => Math.min(1, Math.max(0, amount)) * 0.01;

/**
 * Sideways aberration per channel in whole pixels for a box `width` wide
 * (ffmpeg rgbashift rh/gh/bh; the preview samples channel c at x − shift[c]).
 */
export function aberrationShifts(amount: number, rgb: readonly number[], width: number): [number, number, number] {
  const base = aberrationShift(amount) * width;
  return [0, 1, 2].map((i) => {
    const v = Math.round(Math.abs(rgb[i]) * base);
    return rgb[i] < 0 ? -v : v;
  }) as [number, number, number];
}

/**
 * Radial aberration: how much each channel is magnified about the centre
 * (relative scale; channel c samples the source at centre + d · (1 − scale[c])).
 * At the left and right edges this moves a channel as far as the sideways mode.
 */
export function aberrationScales(amount: number, rgb: readonly number[]): [number, number, number] {
  const s = aberrationShift(amount) * 2;
  return [rgb[0] * s, rgb[1] * s, rgb[2] * s];
}

/** Where channel c samples the source under radial aberration (pixel-centre coordinates). */
export function radialAberrationSource(x: number, y: number, w: number, h: number, scale: number): [number, number] {
  const m = 1 - scale;
  return [w / 2 + (x - w / 2) * m, h / 2 + (y - h / 2) * m];
}

/** Whether grain uses the shaped path (cells, softness, tone weights or a blend) instead of plain per-pixel noise. */
export function grainShaped(g: { grainSize: number; grainSoftness: number; grainTones: readonly number[]; grainBlend: string }): boolean {
  return g.grainSize > 1 || g.grainSoftness > 0 || g.grainTones.some((t) => t !== 1) || g.grainBlend !== 'add';
}

/** Raw noise strength of shaped grain before it is shaped (ffmpeg `noise` maximum: uniform ±50 levels). */
export const GRAIN_RAW_STRENGTH = 100;

/** Gaussian sigma (in the renderer's pixels) softening shaped grain, for a cell of `cellPx` pixels. */
export const grainSigma = (softness: number, cellPx: number) => 2 * Math.min(1, Math.max(0, softness)) * Math.max(1, cellPx);

/**
 * Gain that brings shaped grain back to the plain grain's spread: noise of
 * GRAIN_RAW_STRENGTH, upscaled bilinearly from cells of `cellPx` and blurred
 * by `sigmaPx`, loses about this much of its standard deviation.
 */
export function grainGain(amount: number, cellPx: number, sigmaPx: number): number {
  return grainStrength(amount) / GRAIN_RAW_STRENGTH / grainKept(cellPx, sigmaPx);
}

const keptCache = new Map<string, number>();

/**
 * Standard deviation kept by shaped grain's spatial filtering: iid noise in
 * cells of `cellPx` pixels, interpolated linearly at pixel centres, then a
 * gaussian of `sigmaPx` (truncated at 2.5σ like the preview's blur). Exact
 * per axis (the sum of the squared weights on the cells, averaged over a
 * cell), the two axes multiply.
 */
export function grainKept(cellPx: number, sigmaPx: number): number {
  const s = Math.max(1, cellPx);
  const sig = sigmaPx > 0.05 ? Math.max(0.5, sigmaPx) : 0;
  const key = `${s.toFixed(3)}:${sig.toFixed(3)}`;
  const hit = keptCache.get(key);
  if (hit !== undefined) return hit;
  const r = sig ? Math.min(32, Math.ceil(sig * 2.5)) : 0;
  const g: number[] = [];
  for (let i = -r; i <= r; i++) g.push(sig ? Math.exp(-(i * i) / (2 * sig * sig)) : 1);
  const gs = g.reduce((a, b) => a + b, 0);
  const span = Math.max(1, Math.round(s * 4));
  let total = 0;
  for (let x = 0; x < span; x++) {
    const w = new Map<number, number>();
    for (let i = -r; i <= r; i++) {
      const q = (x + i + 0.5) / s - 0.5;
      const j = Math.floor(q);
      const f = q - j;
      w.set(j, (w.get(j) ?? 0) + (g[i + r] / gs) * (1 - f));
      w.set(j + 1, (w.get(j + 1) ?? 0) + (g[i + r] / gs) * f);
    }
    let v = 0;
    for (const c of w.values()) v += c * c;
    total += v;
  }
  const perAxis = total / span;
  const kept = perAxis; // std: the square root of the 2D variance factor perAxis²
  if (keptCache.size > 200) keptCache.clear();
  keptCache.set(key, kept);
  return kept;
}

/**
 * Tone weight of shaped grain at luma `l` (0..1): shadows below the 0.5
 * midpoint, highlights above it, midtones in between (smoothstep crossfades).
 */
export function grainToneWeight(l: number, tones: readonly number[]): number {
  const c = Math.min(1, Math.max(0, l));
  const ss = (e0: number, e1: number, x: number) => {
    const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
  };
  const lo = ss(0, 0.5, c);
  const hi = ss(0.5, 1, c);
  return tones[0] * (1 - lo) + tones[1] * (lo - hi) + tones[2] * hi;
}

/** How much grain a pixel of luma `l` takes under a blend mode (film: c + n·(1 − c/2); overlay: c + n·4c(1 − c)). */
export function grainBlendFactor(l: number, blend: string): number {
  const c = Math.min(1, Math.max(0, l));
  return blend === 'film' ? 1 - 0.5 * c : blend === 'overlay' ? 4 * c * (1 - c) : 1;
}

/**
 * Shaped grain on luma `l` (0..1) for signed noise `n` (luma units, zero
 * mean): the same formula the preview shader and the export's lut2 use. A
 * flat area keeps its mean since the weight only depends on the picture.
 */
export function grainLuma(l: number, n: number, tones: readonly number[], blend: string): number {
  return l + n * grainToneWeight(l, tones) * grainBlendFactor(l, blend);
}

/** Lens distortion → ffmpeg `lenscorrection` k1 (k2 = 0). */
export const lensK1 = (amount: number) => Math.min(1, Math.max(-1, amount)) * 0.5;

/**
 * Where an output pixel samples the source under lens distortion (ffmpeg
 * lenscorrection: radius normalised by half the diagonal, centre in the middle).
 * Pixel coordinates; returns source coordinates.
 */
export function lensSource(x: number, y: number, w: number, h: number, k1: number): [number, number] {
  const cx = w / 2;
  const cy = h / 2;
  const dx = x - cx;
  const dy = y - cy;
  const r2 = ((dx * dx + dy * dy) * 4) / (w * w + h * h);
  const m = 1 + k1 * r2;
  return [cx + dx * m, cy + dy * m];
}

/** Glow per channel: the picture screened with the blurred highlights above the threshold, by `amount`. */
export function glowChannel(c: number, blurredHighlight: number, amount: number): number {
  const screen = c + blurredHighlight - c * blurredHighlight;
  return c + (screen - c) * amount;
}

/** The highlight pass of the glow (ffmpeg colorlevels with the threshold as black input). */
export const glowHighlight = (c: number, threshold: number) => Math.min(1, Math.max(0, (c - threshold) / (1 - threshold)));

// ---------------------------------------------------------------------------
// Look presets built from the grade controls
// ---------------------------------------------------------------------------

export interface GradePreset {
  id: string;
  label: string;
  description: string;
  grade: GradeData;
  /** Basic adjustments it also sets (others are reset to neutral). */
  color?: Partial<{ contrast: number; saturation: number; brightness: number }>;
}

const S_CURVE: CurvePoint[] = [
  [0, 0],
  [0.25, 0.2],
  [0.75, 0.82],
  [1, 1],
];

export const GRADE_PRESETS: readonly GradePreset[] = [
  {
    id: 'teal-orange',
    label: 'Teal & orange',
    description: 'Teal shadows, warm skin and highlights.',
    grade: { lift: { r: -0.04, g: 0.01, b: 0.06, y: 0 }, gain: { r: 0.1, g: 0.02, b: -0.12, y: 0 }, colorBoost: 0.25, curves: { master: S_CURVE } },
  },
  {
    id: 'bleach-bypass',
    label: 'Bleach bypass',
    description: 'Silvery, low saturation and hard contrast.',
    grade: { colorBoost: -0.6, curves: { master: [[0, 0], [0.2, 0.12], [0.8, 0.9], [1, 1]] }, highlights: 0.2 },
    color: { saturation: 0.6 },
  },
  {
    id: 'film-noir',
    label: 'Film noir',
    description: 'Black and white with deep blacks.',
    grade: { lift: { r: 0, g: 0, b: 0, y: -0.04 }, curves: { master: [[0, 0], [0.3, 0.2], [0.7, 0.8], [1, 1]] } },
    color: { saturation: 0, contrast: 1.15 },
  },
  {
    id: 'golden-hour',
    label: 'Golden hour',
    description: 'Warm, soft sunlight.',
    grade: { gain: { r: 0.12, g: 0.04, b: -0.14, y: 0.1 }, gamma: { r: 0.03, g: 0.01, b: -0.05, y: 0.05 }, tint: 0.1, shadows: 0.15, colorBoost: 0.15 },
  },
  {
    id: 'moonlight',
    label: 'Moonlight',
    description: 'Cool, dark night look.',
    grade: { lift: { r: -0.03, g: 0, b: 0.06, y: -0.03 }, gain: { r: -0.12, g: 0, b: 0.15, y: -0.3 }, shadows: -0.3, colorBoost: -0.3 },
  },
  {
    id: 'faded-film',
    label: 'Faded film',
    description: 'Lifted blacks, soft highlights, gentle colour.',
    grade: { lift: { r: 0.01, g: 0, b: 0.02, y: 0.08 }, curves: { master: [[0, 0], [1, 0.92]] }, colorBoost: -0.25, gain: { r: 0.04, g: 0.02, b: -0.04, y: 0 } },
  },
  {
    id: 'punchy',
    label: 'Punchy',
    description: 'More contrast and vibrant colour.',
    grade: { curves: { master: S_CURVE }, colorBoost: 0.45, highlights: -0.1 },
  },
  {
    id: 'cross-process',
    label: 'Cross process',
    description: 'Yellow-green highlights and blue shadows.',
    grade: {
      curves: {
        r: [[0, 0], [0.25, 0.18], [0.75, 0.85], [1, 1]],
        g: [[0, 0.04], [0.5, 0.55], [1, 1]],
        b: [[0, 0.12], [1, 0.82]],
      },
      colorBoost: 0.2,
    },
  },
  {
    id: 'greens-pop',
    label: 'Lush greens',
    description: 'Richer foliage and skies, skin untouched.',
    grade: {
      hueSat: [[0.1, 0], [0.3, 0.45], [0.58, 0.3], [0.75, 0]],
      hueHue: [[0.1, 0], [0.22, -0.04], [0.4, 0]],
    },
  },
];

export function findGradePreset(id: string): GradePreset | undefined {
  return GRADE_PRESETS.find((p) => p.id === id);
}
